import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  FolderOpen as FilesIcon,
  LocalPostOffice as MailIcon,
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import axios from 'axios';
import LetterTemplatePicker from './LetterTemplatePicker';
import LetterPreview from './LetterPreview';
import ComposeEmailDialog from '../email/ComposeEmailDialog';

const STEPS = ['Client & Template', 'Edit Content', 'Preview', 'Actions'];

function EditorToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, onClick, label) => (
    <Button
      key={label}
      size="small"
      variant={active ? 'contained' : 'outlined'}
      onClick={onClick}
      sx={{ minWidth: 32, px: 1, py: 0.25, fontSize: 12 }}
    >
      {label}
    </Button>
  );
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'B')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'I')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), 'U')}
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), '• List')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), '1. List')}
    </Box>
  );
}

function Step1({ form, onChange, clients, loadingClients }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Autocomplete
        options={clients}
        getOptionLabel={(o) => o.display_name || `${o.first_name || ''} ${o.last_name || ''}`.trim()}
        value={clients.find((c) => c.id === form.client_id) || null}
        onChange={(_, val) => onChange({ client_id: val?.id || null })}
        loading={loadingClients}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Client"
            required
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingClients && <CircularProgress size={14} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(o, v) => o.id === v.id}
      />
      <TextField
        size="small"
        label="Letter Title"
        required
        value={form.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder="e.g. 2024 Engagement Letter"
        fullWidth
      />
      <Typography variant="subtitle2" fontWeight={700}>Select a Template</Typography>
      <LetterTemplatePicker
        selected={form.template}
        onSelect={(tpl) => onChange({ template: tpl })}
      />
    </Box>
  );
}

function Step2({ editor }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Edit the letter content below. Merge tags like <code>{'{{client.full_name}}'}</code> will be
        resolved when the PDF is generated.
      </Typography>
      <EditorToolbar editor={editor} />
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: 320,
          px: 2,
          py: 1.5,
          '& .ProseMirror': { outline: 'none', minHeight: 300 },
          '& .ProseMirror p': { my: 0.5 },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}

function Step3({ letterId, firmInfo }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is how the letter will look when generated as a PDF.
      </Typography>
      <LetterPreview letterId={letterId} firmInfo={firmInfo} />
    </Box>
  );
}

function Step4({ actions, onAction, saving }) {
  const ACTION_OPTIONS = [
    { value: 'generate',       icon: <PdfIcon fontSize="small" />,   label: 'Generate PDF' },
    { value: 'generate_email', icon: <EmailIcon fontSize="small" />, label: 'Generate + Email' },
    { value: 'generate_files', icon: <FilesIcon fontSize="small" />, label: 'Generate + Save to Files' },
    { value: 'generate_mail',  icon: <MailIcon fontSize="small" />,  label: 'Generate + Queue for Mail' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Choose what to do after generating the PDF:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {ACTION_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            variant={actions === opt.value ? 'contained' : 'outlined'}
            startIcon={opt.icon}
            onClick={() => onAction(opt.value)}
            disabled={saving}
            sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
          >
            {opt.label}
          </Button>
        ))}
      </Box>

      {actions === 'generate_mail' && (
        <Alert severity="info" sx={{ mt: 1 }}>
          You'll be prompted for the mailing address on the next step.
        </Alert>
      )}
    </Box>
  );
}

export default function CreateLetterDialog({ open, onClose, initialClientId, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    client_id: initialClientId || null,
    title: '',
    template: null,
  });
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [letterId, setLetterId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState('generate');
  const [snack, setSnack] = useState(null);
  const [firmInfo, setFirmInfo] = useState(null);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);
  const [composeEmailProps, setComposeEmailProps] = useState({});

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: '',
  });

  useEffect(() => {
    if (!open) {
      setStep(0);
      setForm({ client_id: initialClientId || null, title: '', template: null });
      setLetterId(null);
      setSaving(false);
      setAction('generate');
      if (editor) editor.commands.setContent('');
      return;
    }

    // Load clients
    setLoadingClients(true);
    const token = localStorage.getItem('access_token');
    axios.get('/api/clients/', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setClients(r.data?.results || r.data || []))
      .catch(() => {})
      .finally(() => setLoadingClients(false));

    // Load firm info
    axios.get('/api/company-information/', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setFirmInfo(r.data))
      .catch(() => {});
  }, [open, initialClientId]);

  // When template is selected, populate the editor with its body
  useEffect(() => {
    if (form.template && editor) {
      editor.commands.setContent(form.template.body_template || '');
      if (!form.title && form.template.subject_template) {
        setForm((f) => ({ ...f, title: f.title || form.template.subject_template }));
      }
    }
  }, [form.template]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canNext = () => {
    if (step === 0) return form.client_id && form.title.trim();
    return true;
  };

  const saveDraft = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const payload = {
      client_id: form.client_id,
      title: form.title.trim(),
      template_id: form.template?.id || null,
      content_html: editor ? editor.getHTML() : '',
    };

    if (letterId) {
      const r = await axios.patch(`/api/letters/${letterId}/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.data;
    } else {
      const r = await axios.post('/api/letters/', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLetterId(r.data.id);
      return r.data;
    }
  }, [form, letterId, editor]);

  const handleNext = async () => {
    if (step === 1) {
      // Save draft when leaving the edit step
      try {
        setSaving(true);
        await saveDraft();
      } catch (e) {
        setSnack({ severity: 'error', message: 'Failed to save draft.' });
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    setStep((s) => s + 1);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Ensure draft is saved
      const letterData = await saveDraft();
      const lid = letterId || letterData.id;

      // Generate PDF
      const genRes = await axios.post(`/api/letters/${lid}/generate/`, {}, { headers });
      const generated = genRes.data;

      if (action === 'generate') {
        setSnack({ severity: 'success', message: 'PDF generated successfully.' });
        if (onCreated) onCreated(generated);
        setTimeout(onClose, 1200);

      } else if (action === 'generate_email') {
        setSnack({ severity: 'success', message: 'PDF generated — opening email compose…' });
        setComposeEmailProps({
          initialSubject: form.title,
          initialClientId: form.client_id,
          attachmentLetterId: lid,
        });
        setComposeEmailOpen(true);
        if (onCreated) onCreated(generated);
        onClose();

      } else if (action === 'generate_files') {
        await axios.post(`/api/letters/${lid}/save-to-files/`, {}, { headers });
        const clientName = clients.find((c) => c.id === form.client_id);
        const name = clientName
          ? (clientName.display_name || `${clientName.first_name || ''} ${clientName.last_name || ''}`.trim())
          : 'client';
        setSnack({ severity: 'success', message: `Letter saved to ${name}'s Files.` });
        if (onCreated) onCreated(generated);
        setTimeout(onClose, 1500);

      } else if (action === 'generate_mail') {
        const addr = window.prompt('Enter mailing address (street, city, state, zip):');
        if (addr) {
          await axios.post(`/api/letters/${lid}/queue-mail/`, { mailing_address: { raw: addr } }, { headers });
          setSnack({ severity: 'info', message: `Letter queued for mailing to ${addr}.` });
          if (onCreated) onCreated(generated);
          setTimeout(onClose, 1500);
        }
      }
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.error || 'Failed to generate letter.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '65vh' } }}>
        <DialogTitle>
          <Typography fontWeight={700}>New Letter</Typography>
          <Stepper activeStep={step} sx={{ mt: 1.5 }} alternativeLabel>
            {STEPS.map((s) => (
              <Step key={s}>
                <StepLabel><Typography variant="caption">{s}</Typography></StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          {step === 0 && (
            <Step1
              form={form}
              onChange={update}
              clients={clients}
              loadingClients={loadingClients}
            />
          )}
          {step === 1 && <Step2 editor={editor} />}
          {step === 2 && <Step3 letterId={letterId} firmInfo={firmInfo} />}
          {step === 3 && (
            <Step4 actions={action} onAction={setAction} saving={saving} />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={step === 0 ? onClose : () => setStep((s) => s - 1)} disabled={saving}>
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {step < STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canNext() || saving}
              >
                {saving ? <CircularProgress size={14} color="inherit" /> : 'Next'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleFinish}
                disabled={saving}
              >
                {saving ? <CircularProgress size={14} color="inherit" /> : 'Generate'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        )}
      </Snackbar>

      {composeEmailOpen && (
        <ComposeEmailDialog
          open={composeEmailOpen}
          onClose={() => setComposeEmailOpen(false)}
          onSent={() => setComposeEmailOpen(false)}
          {...composeEmailProps}
        />
      )}
    </>
  );
}
